import { computed, type Ref, toValue } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { useApi } from '@shopexperts/shared';
import type { PublicExpertMinimal, PackagedServiceMinimal } from '@shopexperts/shared';

export interface UseExpertMinimalOptions {
  expertSlug: Ref<string | undefined> | (() => string | undefined);
  packagedServiceSlug?: Ref<string | undefined> | (() => string | undefined);
}

/**
 * Composable for fetching minimal expert details with optional packaged service.
 * 
 * Used in the lead submission form to display the preferred expert card
 * with an optional pre-selected packaged service.
 * 
 * @param options - Options object containing expertSlug and optional packagedServiceSlug
 * 
 * @example
 * ```ts
 * const { expert, packagedService, isLoading } = useExpertMinimal({
 *   expertSlug: () => props.expertId,
 *   packagedServiceSlug: () => props.packagedServiceSlug,
 * });
 * ```
 */
export function useExpertMinimal(options: UseExpertMinimalOptions) {
  const api = useApi();

  const expertSlug = computed(() => toValue(options.expertSlug));
  const packagedServiceSlug = computed(() => 
    options.packagedServiceSlug ? toValue(options.packagedServiceSlug) : undefined
  );

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: computed(() => ['expert', 'minimal', expertSlug.value, packagedServiceSlug.value]),
    queryFn: async () => {
      const currentSlug = expertSlug.value;
      if (!currentSlug) return null;

      return await api.public.expert.getMinimalExpert({
        params: { slug: currentSlug },
        query: packagedServiceSlug.value 
          ? { packaged_service_slug: packagedServiceSlug.value }
          : undefined,
      });
    },
    enabled: () => !!expertSlug.value,
    staleTime: 60000, // 1 minute
  });

  const expert = computed<PublicExpertMinimal | null>(() => data.value?.expert ?? null);
  const packagedService = computed<PackagedServiceMinimal | null>(() => data.value?.packaged_service ?? null);

  return {
    expert,
    packagedService,
    data,
    isLoading,
    isError,
    error,
    refetch,
  };
}

